import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import styles from 'assets/jss/material-dashboard-pro-react/components/servicePickerStyle';
import { makeStyles } from '@material-ui/core';
import ArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import ArrowRight from '@material-ui/icons/KeyboardArrowRight';
import classNames from 'classnames';
import Slider from './subcomponents/Slider';
import SingleSlide from './subcomponents/SingleSlide';
import SliderWrapper from './subcomponents/SliderWrapper';
import { debounce } from 'lodash';
import { getLanguage } from 'utils/language/language';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';

// @ts-ignore
const useStyles = makeStyles(styles);

const MAX_SINGLE_SLIDE_WIDTH = 200;

interface IServicePickerProps {
	selectedTab: number | null;
	setSelectedTab: (selectedTabNumber: number | null) => void;
	tabs: types.newServices.IServicePickerTab[];
	popularColor?: types.newServices.servicePickerColor;
	selectedTabColor?: types.newServices.servicePickerColor;
	numberOfTabsOnSlide: number;
}

const ServicePicker = ({
	selectedTab,
	setSelectedTab,
	tabs,
	popularColor = 'primary',
	numberOfTabsOnSlide,
	selectedTabColor,
}: IServicePickerProps) => {
	const classes = useStyles();
	const language = getLanguage();
	const [currentPage, setCurrentPage] = useState<number>(0);
	const [sliderWrapperWidth, setSliderWrapperWidth] = useState<number>(0);
	const sliderWrapperRef = useRef<HTMLDivElement>(null);
	const arrowsHidden: boolean = tabs.length <= numberOfTabsOnSlide;

	const onResize = useCallback(() => {
		const width = sliderWrapperRef?.current?.offsetWidth || 0;
		const maxWidth = tabs.length < 2 ? 4 * MAX_SINGLE_SLIDE_WIDTH : tabs.length * MAX_SINGLE_SLIDE_WIDTH;
		setSliderWrapperWidth(Math.min(width, maxWidth));
	}, [tabs]);

	useEffect(() => {
		onResize();
	}, [onResize]);

	useEffect(() => {
		const handleResize = debounce(onResize, 300);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [onResize]);

	useEffect(() => {
		if (selectedTab !== null && selectedTab + 1 > tabs.length) {
			setSelectedTab(null);
		}
		// eslint-disable-next-line
	}, [selectedTab, tabs]);

	const numberOfPages: number = useMemo(() => {
		return Math.ceil(tabs.length / numberOfTabsOnSlide);
	}, [tabs, numberOfTabsOnSlide]);

	const arrowLeftClasses = classNames(classes.arrowButton, classes.arrowLeftButton, {
		[classes.arrowButtonDisabled]: currentPage === 0,
		[classes.hideArrows]: arrowsHidden || currentPage === 0,
	});

	const arrowRightClasses = classNames(classes.arrowButton, classes.arrowRightButton, {
		[classes.arrowButtonDisabled]: currentPage === numberOfPages - 1,
		[classes.hideArrows]: arrowsHidden || currentPage === numberOfPages - 1,
	});

	const singleSlideWidth: number = useMemo(() => {
		return sliderWrapperWidth / numberOfTabsOnSlide;
	}, [sliderWrapperWidth, numberOfTabsOnSlide]);

	const next = () => {
		setCurrentPage((page) => (page < numberOfPages - 1 ? page + 1 : page));
	};

	const back = () => {
		setCurrentPage((page) => (page > 0 ? page - 1 : page));
	};

	const transparent = useCallback(
		(index: number) => {
			if (numberOfPages === 0) {
				return false;
			}
			if (currentPage !== numberOfPages - 1) {
				return Math.floor(index / numberOfTabsOnSlide) !== currentPage;
			}
			return index < tabs.length - numberOfTabsOnSlide;
		},
		[numberOfTabsOnSlide, tabs, numberOfPages, currentPage]
	);

	return (
		<div className={classes.servicePickerWrapper}>
			<div className={arrowLeftClasses} onClick={back}>
				<ConditionalRender
					show={language === 'en'}
					onTrue={() => <ArrowLeft />}
					onFalse={() => <ArrowRight />}
				/>
			</div>
			<SliderWrapper ref={sliderWrapperRef}>
				<Slider
					currentPage={currentPage}
					wrapperWidth={sliderWrapperWidth}
					numberOfPages={numberOfPages}
					numberOfTabs={tabs.length}
					numberOfTabsOnSlide={numberOfTabsOnSlide}
					arrowsHidden={arrowsHidden}
				>
					{tabs.map((singleTab, index) => (
						<SingleSlide
							key={index}
							tab={singleTab}
							width={tabs.length > 1 ? singleSlideWidth : 400}
							firstSlide={index === 0}
							lastSlide={index === tabs.length - 1}
							onClick={() => setSelectedTab(index)}
							popularColor={popularColor}
							selected={selectedTab === index}
							transparent={transparent(index)}
							selectedTabColor={selectedTabColor}
						/>
					))}
				</Slider>
			</SliderWrapper>
			<div className={arrowRightClasses} onClick={next}>
				<ConditionalRender
					show={language === 'en'}
					onTrue={() => <ArrowRight />}
					onFalse={() => <ArrowLeft />}
				/>
			</div>
		</div>
	);
};

export default ServicePicker;
